import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, API, apiError } from "../lib/api";
import Layout from "../components/Layout";
import ResourceContent from "../components/ResourceContent";
import ImagePickerModal from "../components/ImagePickerModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Presentation, FileQuestion, NotebookPen, Trash2, Eye, Globe, Lock, Download, FileText, FolderPlus, Folder, FolderOpen, ScanLine, PenLine } from "lucide-react";

const meta = {
  presentation: { icon: Presentation, label: "Презентация", color: "bg-sky-100 text-sky-600" },
  worksheet: { icon: FileQuestion, label: "Работен лист", color: "bg-emerald-100 text-emerald-600" },
  "lesson-plan": { icon: NotebookPen, label: "План на урок", color: "bg-amber-100 text-amber-600" },
  grading: { icon: ScanLine, label: "Проверен тест", color: "bg-rose-100 text-rose-600" },
};

export default function MyResources() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [active, setActive] = useState(null);
  const [imgDialog, setImgDialog] = useState({ open: false, slideIdx: null });
  const [dirty, setDirty] = useState(false);

  const load = () => api.get("/resources").then(({ data }) => setItems(data)).catch(() => {});
  const loadFolders = () => api.get("/folders").then(({ data }) => setFolders(data)).catch(() => {});
  useEffect(() => { load(); loadFolders(); }, []);

  const del = async (id, e) => { e.stopPropagation(); await api.delete(`/resources/${id}`); setItems(items.filter((i) => i.id !== id)); toast.success("Изтрито"); };
  const togglePublish = async (id, e) => {
    e.stopPropagation();
    const { data } = await api.patch(`/resources/${id}/publish`);
    setItems(items.map((i) => (i.id === id ? { ...i, published: data.published } : i)));
    toast.success(data.published ? "Публикувано за други учители" : "Направено лично");
  };
  const moveToFolder = async (id, folderId, e) => {
    e.stopPropagation();
    await api.patch(`/resources/${id}`, { folder_id: folderId || null });
    setItems(items.map((i) => (i.id === id ? { ...i, folder_id: folderId || null } : i)));
    toast.success("Преместено");
  };
  const createFolder = async () => {
    const name = window.prompt("Име на папката (напр. 5А клас, Математика):");
    if (!name?.trim()) return;
    const { data } = await api.post("/folders", { name: name.trim() });
    setFolders([...folders, data]);
    toast.success("Папката е създадена");
  };
  const deleteFolder = async (fid) => {
    await api.delete(`/folders/${fid}`);
    setFolders(folders.filter((f) => f.id !== fid));
    if (activeFolder === fid) setActiveFolder("all");
    load();
  };

  const pickImage = (url) => {
    setActive((r) => {
      const slides = [...r.content.slides];
      slides[imgDialog.slideIdx] = { ...slides[imgDialog.slideIdx], image_url: url };
      return { ...r, content: { ...r.content, slides } };
    });
    setDirty(true);
  };
  const saveImages = async () => {
    try {
      const { data } = await api.patch(`/resources/${active.id}`, { content: active.content });
      setItems(items.map((i) => (i.id === data.id ? data : i)));
      setDirty(false);
      toast.success("Запазено");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const filtered = activeFolder === "all" ? items : items.filter((i) => (i.folder_id || null) === (activeFolder === "none" ? null : activeFolder));

  return (
    <Layout>
      <div className="max-w-5xl">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900">💾 Моите материали</h1>
        <p className="text-slate-500 mt-2">Всичко, което си създал, организирано по папки за твоите класове.</p>

        {/* Folder tabs */}
        <div className="flex flex-wrap items-center gap-2 mt-6">
          {[{ id: "all", name: "Всички" }, { id: "none", name: "Без папка" }, ...folders].map((f) => (
            <div key={f.id} className={`group flex items-center rounded-full ${activeFolder === f.id ? "bg-sky-500 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
              <button data-testid={`folder-${f.id}`} onClick={() => setActiveFolder(f.id)} className="pl-4 pr-2 py-2 text-sm font-bold flex items-center gap-1.5">
                {activeFolder === f.id ? <FolderOpen size={15} /> : <Folder size={15} />} {f.name}
              </button>
              {!["all", "none"].includes(f.id) && (
                <button data-testid={`delete-folder-${f.id}`} onClick={() => deleteFolder(f.id)} className="pr-3 pl-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
          <button data-testid="create-folder-btn" onClick={createFolder} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-sky-600 bg-sky-50 hover:bg-sky-100 transition-colors">
            <FolderPlus size={15} /> Нова папка
          </button>
        </div>

        {filtered.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-3xl p-12 text-center mt-6">
            <p className="text-slate-400">Няма материали тук. Създай нов с AI генераторите или премести някой в тази папка!</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {filtered.map((it, i) => {
            const m = meta[it.type] || meta.presentation;
            return (
              <motion.div key={it.id} data-testid={`resource-card-${it.id}`} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
                onClick={() => { setActive(it); setDirty(false); }}
                className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.color}`}><m.icon size={18} /></div>
                  {it.published ? <Globe size={16} className="text-emerald-500" /> : <Lock size={16} className="text-slate-300" />}
                </div>
                <div className="text-xs font-bold text-slate-400 uppercase">{m.label}</div>
                <h3 className="font-heading font-bold text-slate-900 mt-1 line-clamp-2">{it.title}</h3>

                <select data-testid={`move-folder-${it.id}`} value={it.folder_id || ""} onClick={(e) => e.stopPropagation()} onChange={(e) => moveToFolder(it.id, e.target.value, e)}
                  className="mt-3 w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 outline-none">
                  <option value="">📂 Без папка</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>

                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                  <button data-testid={`view-${it.id}`} className="text-slate-400 hover:text-sky-500 transition-colors"><Eye size={17} /></button>
                  {it.type === "presentation" ? (
                    <a data-testid={`dl-${it.id}`} href={`${API}/resources/${it.id}/pptx`} onClick={(e) => e.stopPropagation()} title="PowerPoint" className="text-slate-400 hover:text-sky-500 transition-colors">
                      <Download size={17} />
                    </a>
                  ) : (
                    <>
                      <a data-testid={`dl-pdf-${it.id}`} href={`${API}/resources/${it.id}/pdf`} onClick={(e) => e.stopPropagation()} title="Изтегли PDF" className="text-slate-400 hover:text-sky-500 transition-colors">
                        <FileText size={17} />
                      </a>
                      <a data-testid={`dl-docx-${it.id}`} href={`${API}/resources/${it.id}/docx`} onClick={(e) => e.stopPropagation()} title="Редактирай в Word" className="text-slate-400 hover:text-indigo-500 transition-colors">
                        <PenLine size={17} />
                      </a>
                    </>
                  )}
                  <button data-testid={`publish-${it.id}`} onClick={(e) => togglePublish(it.id, e)} className="text-slate-400 hover:text-emerald-500 transition-colors"><Globe size={17} /></button>
                  <button data-testid={`delete-${it.id}`} onClick={(e) => del(it.id, e)} className="text-slate-400 hover:text-red-500 transition-colors ml-auto"><Trash2 size={17} /></button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDirty(false); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center justify-between gap-4 pr-6">
              <span>{active?.title}</span>
              <div className="flex items-center gap-2 shrink-0">
                {active?.type === "presentation" && dirty && (
                  <button data-testid="modal-save-images" onClick={saveImages} className="bg-amber-400 text-white text-sm font-bold px-4 py-2 rounded-full">Запази</button>
                )}
                {active?.type === "presentation" ? (
                  <a data-testid="modal-download-pptx" href={`${API}/resources/${active.id}/pptx`} className="flex items-center gap-2 bg-sky-500 text-white text-sm font-bold px-4 py-2 rounded-full"><Download size={15} /> .pptx</a>
                ) : (
                  <>
                    <a data-testid="modal-download-pdf" href={`${API}/resources/${active?.id}/pdf`} className="flex items-center gap-2 bg-sky-500 text-white text-sm font-bold px-4 py-2 rounded-full"><FileText size={15} /> PDF</a>
                    <a data-testid="modal-download-docx" href={`${API}/resources/${active?.id}/docx`} className="flex items-center gap-2 bg-indigo-500 text-white text-sm font-bold px-4 py-2 rounded-full"><PenLine size={15} /> Word</a>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <ResourceContent resource={active} onEditImage={active?.type === "presentation" ? (i) => setImgDialog({ open: true, slideIdx: i }) : undefined} />
          </div>
        </DialogContent>
      </Dialog>

      <ImagePickerModal open={imgDialog.open} onOpenChange={(o) => setImgDialog({ ...imgDialog, open: o })} onSelect={pickImage} />
    </Layout>
  );
}
